/** Sticky table headers that stay below site header + tab bar while vertically scrolling. */

import {
  applyPinnedColumnSticky,
  syncPinnedColumnStickyToFloat,
} from "./pinnedColumns";

export interface StickyTableHeadHandle {
  sync: () => void;
  teardown: () => void;
}

const STUCK_CLASS = "sfe-sticky-tab-nav--stuck";

export type MeasureStickyChromeOptions = {
  /** Include secondary panel chrome (e.g. Agentic title/tabs) below the tab strip. */
  includeSecondaryChrome?: boolean;
};

/** Live bottom edge of sticky site chrome (header + tab strip [+ secondary panels]). */
export function measureStickyChromeBottom(opts: MeasureStickyChromeOptions = {}): number {
  const includeSecondary = opts.includeSecondaryChrome !== false;

  // Web: anchor table heads directly under the visible tab strip.
  if (document.body.classList.contains("sfe-standalone-host")) {
    const nav = document.querySelector<HTMLElement>(".sfe-web-wheel-subnav");
    if (nav) {
      const rect = nav.getBoundingClientRect();
      if (rect.bottom > 0 && rect.top < 160) {
        return Math.ceil(rect.bottom);
      }
    }
  }

  let bottom = 0;
  const nav =
    document.querySelector<HTMLElement>(".sfe-web-wheel-subnav") ||
    document.querySelector<HTMLElement>(".sfe-sticky-tab-nav") ||
    document.querySelector<HTMLElement>(".wheel-subnav");
  const navStuck =
    nav instanceof HTMLElement &&
    (nav.classList.contains("is-stuck") ||
      nav.classList.contains("sfe-sticky-tab-nav--stuck") ||
      nav.classList.contains(STUCK_CLASS));
  const webTabCoversHeader =
    document.body.classList.contains("sfe-standalone-host") &&
    navStuck &&
    nav instanceof HTMLElement &&
    nav.getBoundingClientRect().top <= 1;

  const header = document.querySelector("header.top");
  if (header instanceof HTMLElement && !webTabCoversHeader) {
    const rect = header.getBoundingClientRect();
    if (rect.bottom > 0) bottom = Math.max(bottom, rect.bottom);
  }

  for (const selector of [".wheel-subnav", ".sfe-web-wheel-subnav", ".sfe-sticky-tab-nav"]) {
    const el = document.querySelector(selector);
    if (!(el instanceof HTMLElement)) continue;
    const rect = el.getBoundingClientRect();
    const stuck =
      el.classList.contains("is-stuck") ||
      el.classList.contains("sfe-sticky-tab-nav--stuck") ||
      el.classList.contains(STUCK_CLASS);
    if (rect.bottom <= 0) continue;
    if (stuck || rect.top >= 0) bottom = Math.max(bottom, rect.bottom);
  }

  if (includeSecondary) {
    const agents = document.querySelector(".sfe-agents-sticky-chrome");
    if (agents instanceof HTMLElement) {
      const rect = agents.getBoundingClientRect();
      if (rect.bottom > 0 && (rect.top >= 0 || getComputedStyle(agents).position === "sticky")) {
        bottom = Math.max(bottom, rect.bottom);
      }
    }
  }

  return Math.ceil(bottom);
}

/** Publish CSS vars so panel sticky chrome / table heads share Analytics+ offsets. */
export function syncStickyChromeOffsets(): void {
  const primary = measureStickyChromeBottom({ includeSecondaryChrome: false });
  const withSecondary = measureStickyChromeBottom({ includeSecondaryChrome: true });
  const root = document.documentElement;
  root.style.setProperty("--sfe-agents-sticky-top", `${primary}px`);
  root.style.setProperty("--sfe-table-sticky-top", `${withSecondary}px`);

  const nav =
    document.querySelector<HTMLElement>(".sfe-web-wheel-subnav") ||
    document.querySelector<HTMLElement>(".sfe-sticky-tab-nav") ||
    document.querySelector<HTMLElement>(".wheel-subnav");
  if (nav) {
    const height = Math.ceil(nav.getBoundingClientRect().height);
    if (height > 0) root.style.setProperty("--wheel-subnav-height", `${height}px`);
  }
}

function resolveScrollTargets(table: HTMLTableElement): {
  wrap: HTMLElement;
  hscroll: HTMLElement | null;
} {
  const recsWrap = table.closest(".recs-wrap");
  if (recsWrap instanceof HTMLElement) {
    const hscroll = table.closest(".recs-hscroll");
    return { wrap: recsWrap, hscroll: hscroll instanceof HTMLElement ? hscroll : null };
  }

  // Keep the sticky float outside overflow-x hosts — otherwise sticky is trapped
  // in the horizontal scroller and won't pin under the tab strip on page scroll.
  const incomeWrap = table.closest(".sfe-income-txn-wrap");
  if (incomeWrap instanceof HTMLElement) {
    const parent = incomeWrap.parentElement;
    if (parent instanceof HTMLElement) {
      return { wrap: parent, hscroll: incomeWrap };
    }
    return { wrap: incomeWrap, hscroll: incomeWrap };
  }

  const callsWrap = table.closest(".sfe-calls-plus-table-wrap");
  if (callsWrap instanceof HTMLElement) {
    const parent = callsWrap.parentElement;
    if (parent instanceof HTMLElement) {
      return { wrap: parent, hscroll: callsWrap };
    }
    return { wrap: callsWrap, hscroll: callsWrap };
  }

  const scrollWrap = table.closest(".sfe-table-scroll, .sfe-tax-table-wrap");
  if (scrollWrap instanceof HTMLElement) {
    return { wrap: scrollWrap, hscroll: scrollWrap };
  }

  const parent = table.parentElement;
  if (parent instanceof HTMLElement) {
    const scrollsX = parent.scrollWidth > parent.clientWidth + 1;
    return { wrap: parent, hscroll: scrollsX ? parent : null };
  }

  return { wrap: table, hscroll: null };
}

function prepareWrap(wrap: HTMLElement, hscroll: HTMLElement | null): void {
  if (wrap.classList.contains("recs-wrap")) {
    wrap.style.overflow = "visible";
  }
  // Day / section hosts for Analytics+ txn tables — never clip sticky heads.
  if (
    wrap.classList.contains("sfe-income-day") ||
    wrap.classList.contains("sfe-income-days") ||
    wrap.classList.contains("sfe-income-month-detail")
  ) {
    wrap.style.overflow = "visible";
  }
  if (hscroll?.classList.contains("recs-hscroll")) return;
  if (hscroll === wrap) {
    wrap.style.overflowX = "auto";
    wrap.style.overflowY = "visible";
  }
}

export function mountStickyTableHead(table: HTMLTableElement): StickyTableHeadHandle {
  const thead = table.tHead;
  if (!thead?.rows.length) {
    return { sync: () => {}, teardown: () => {} };
  }

  const { wrap, hscroll } = resolveScrollTargets(table);
  prepareWrap(wrap, hscroll);

  const existing =
    wrap.querySelector<HTMLElement>(
      `.recs-head-float[data-sfe-for="${table.id || table.dataset.sfeStickyId || ""}"]`,
    ) ??
    document.querySelector<HTMLElement>(
      `.recs-head-float[data-sfe-for="${table.id || table.dataset.sfeStickyId || ""}"]`,
    );
  existing?.remove();

  if (!table.id && !table.dataset.sfeStickyId) {
    table.dataset.sfeStickyId = `sfe-sticky-${Math.random().toString(36).slice(2, 9)}`;
  }
  const tableKey = table.id || table.dataset.sfeStickyId || "";

  const float = document.createElement("div");
  float.className = "recs-head-float sfe-sticky-table-head-float";
  float.dataset.sfeFor = tableKey;
  float.innerHTML = `<div class="recs-head-float-scroll"></div>`;
  const floatScroll = float.querySelector(".recs-head-float-scroll") as HTMLElement;
  const floatTable = document.createElement("table");
  floatScroll.appendChild(floatTable);

  const insertBefore = hscroll ?? table;
  wrap.insertBefore(float, insertBefore);

  let raf = 0;
  let lastHeaderSignature = "";

  const dockFloat = (): void => {
    if (float.parentElement !== wrap) {
      wrap.insertBefore(float, insertBefore);
    }
  };

  const floatToBody = (): void => {
    if (float.parentElement !== document.body) {
      document.body.appendChild(float);
    }
  };

  const firstBodyRow = (): HTMLTableRowElement | null => {
    for (const row of table.querySelectorAll("tbody tr")) {
      if (!(row instanceof HTMLTableRowElement)) continue;
      if (row.classList.contains("sfe-stocks-share-history-row")) continue;
      if (row.classList.contains("sfe-hist-linked-detail")) continue;
      if (row.querySelector("td[colspan]")) continue;
      return row;
    }
    return null;
  };

  const headerSignature = (): string => {
    const headCols = [...thead.querySelectorAll("th")]
      .map((th) => th.getAttribute("data-col") ?? "")
      .join("|");
    const row = firstBodyRow();
    const bodyCols = row
      ? [...row.querySelectorAll("td")].map((td) => td.getAttribute("data-col") ?? "").join("|")
      : "";
    return `${headCols}::${bodyCols}`;
  };

  const syncColgroup = (bodyRow: HTMLTableRowElement | null): void => {
    floatTable.querySelector("colgroup")?.remove();
    const bodyCells = bodyRow
      ? [...bodyRow.querySelectorAll("td")].filter(
          (el): el is HTMLTableCellElement => el instanceof HTMLTableCellElement,
        )
      : [];
    const srcColgroup = table.querySelector("colgroup");

    if (srcColgroup && bodyCells.length === srcColgroup.children.length) {
      const cloned = srcColgroup.cloneNode(true) as HTMLElement;
      const cols = [...cloned.querySelectorAll("col")].filter(
        (el): el is HTMLElement => el instanceof HTMLElement,
      );
      for (let index = 0; index < cols.length; index += 1) {
        const widthPx = bodyCells[index]?.getBoundingClientRect().width;
        if (widthPx) cols[index].style.width = `${widthPx}px`;
      }
      floatTable.insertBefore(cloned, floatTable.firstChild);
      return;
    }

    if (!bodyCells.length) {
      if (srcColgroup) floatTable.insertBefore(srcColgroup.cloneNode(true), floatTable.firstChild);
      return;
    }

    const colgroup = document.createElement("colgroup");
    for (const td of bodyCells) {
      const col = document.createElement("col");
      col.style.width = `${td.getBoundingClientRect().width}px`;
      colgroup.appendChild(col);
    }
    floatTable.insertBefore(colgroup, floatTable.firstChild);
  };

  const syncColumnLayout = (): void => {
    const bodyRow = firstBodyRow();
    const srcThs = [...thead.querySelectorAll("th")].filter(
      (el): el is HTMLTableCellElement => el instanceof HTMLTableCellElement,
    );
    const cloneThs = [...floatTable.querySelectorAll("thead th")].filter(
      (el): el is HTMLTableCellElement => el instanceof HTMLTableCellElement,
    );
    const srcByCol = new Map<string, HTMLTableCellElement>();
    for (const th of srcThs) {
      const col = th.getAttribute("data-col");
      if (col) srcByCol.set(col, th);
    }
    const bodyCells = bodyRow
      ? [...bodyRow.querySelectorAll("td")].filter(
          (el): el is HTMLTableCellElement => el instanceof HTMLTableCellElement,
        )
      : [];

    floatTable.style.tableLayout = "fixed";
    floatTable.style.width = `${table.scrollWidth}px`;
    floatTable.style.removeProperty("transform");
    const tableStyle = getComputedStyle(table);
    floatTable.style.borderCollapse = tableStyle.borderCollapse;
    if (tableStyle.minWidth !== "auto" && tableStyle.minWidth !== "0px") {
      floatTable.style.minWidth = tableStyle.minWidth;
    } else {
      floatTable.style.removeProperty("min-width");
    }

    for (const clone of cloneThs) {
      const col = clone.getAttribute("data-col");
      const bodyTd = col ? bodyCells.find((td) => td.getAttribute("data-col") === col) : undefined;
      const src =
        (col ? srcByCol.get(col) : undefined) ??
        srcThs[cloneThs.indexOf(clone)];
      const widthEl = bodyTd ?? src;
      if (!widthEl || !src) continue;

      const widthPx = widthEl.getBoundingClientRect().width;
      clone.style.width = `${widthPx}px`;
      clone.style.minWidth = `${widthPx}px`;
      clone.style.maxWidth = `${widthPx}px`;

      const cs = getComputedStyle(src);
      clone.style.padding = cs.padding;
      clone.style.textAlign = cs.textAlign;
      clone.style.verticalAlign = cs.verticalAlign;
      clone.style.boxSizing = cs.boxSizing;
    }

    syncColgroup(bodyRow);
  };

  const syncScrollLeft = (): void => {
    if (!hscroll) {
      floatScroll.scrollLeft = 0;
      return;
    }
    floatScroll.scrollLeft = hscroll.scrollLeft;
  };

  const syncFloatTableLayout = (): void => {
    applyPinnedColumnSticky(table);
    syncColumnLayout();
    syncPinnedColumnStickyToFloat(table, floatTable);
    syncScrollLeft();
  };

  const cloneHeader = (): void => {
    floatTable.className = table.className;
    floatTable.replaceChildren();
    // Never duplicate table ids — #wheelPositionsTable etc. must stay on the source table.
    floatTable.removeAttribute("id");
    if (table.id) floatTable.dataset.sfeFloatFor = table.id;
    else floatTable.removeAttribute("data-sfe-float-for");

    const bodyRow = firstBodyRow();
    const srcRow = thead.rows[0] ?? null;
    if (bodyRow && srcRow) {
      const srcByCol = new Map<string, HTMLTableCellElement>();
      for (const th of [...srcRow.cells].filter(
        (el): el is HTMLTableCellElement => el instanceof HTMLTableCellElement,
      )) {
        const col = th.getAttribute("data-col");
        if (col) srcByCol.set(col, th);
      }

      const newThead = document.createElement("thead");
      const newTr = document.createElement("tr");
      for (const td of [...bodyRow.cells].filter(
        (el): el is HTMLTableCellElement => el instanceof HTMLTableCellElement,
      )) {
        const col = td.getAttribute("data-col");
        if (!col) continue;
        const src = srcByCol.get(col);
        if (src) newTr.appendChild(src.cloneNode(true));
      }
      for (const th of [...srcRow.cells].filter(
        (el): el is HTMLTableCellElement => el instanceof HTMLTableCellElement,
      )) {
        const col = th.getAttribute("data-col");
        if (!col || newTr.querySelector(`th[data-col="${col}"]`)) continue;
        newTr.appendChild(th.cloneNode(true));
      }
      newThead.appendChild(newTr);
      floatTable.appendChild(newThead);
    } else {
      floatTable.appendChild(thead.cloneNode(true));
    }

    syncFloatTableLayout();
    lastHeaderSignature = headerSignature();
  };

  const headerColumnCount = (): number => thead.querySelectorAll("th").length;

  const onHScroll = (): void => {
    syncScrollLeft();
  };

  const onFloatScroll = (): void => {
    if (!hscroll) return;
    hscroll.scrollLeft = floatScroll.scrollLeft;
  };

  const syncFloatHostBox = (): void => {
    const host = hscroll ?? wrap;
    const hostRect = host.getBoundingClientRect();
    floatScroll.style.width = `${host.clientWidth}px`;
    if (isWebStandalone()) {
      float.style.left = `${hostRect.left}px`;
      float.style.width = `${hostRect.width}px`;
    }
  };

  const isWebStandalone = (): boolean =>
    document.body.classList.contains("sfe-standalone-host");

  const resetFloatPosition = (): void => {
    dockFloat();
    float.style.position = "";
    float.style.left = "";
    float.style.right = "";
    float.style.width = "";
    float.style.height = "";
    float.style.zIndex = "";
    float.style.removeProperty("top");
  };

  const floatIsReady = (): boolean => floatTable.querySelectorAll("th").length > 0;

  const update = (): void => {
    syncStickyChromeOffsets();
    const top = measureStickyChromeBottom({ includeSecondaryChrome: true });

    const theadRect = thead.getBoundingClientRect();
    const tableRect = table.getBoundingClientRect();
    const headHeight = Math.max(theadRect.height, 24);
    const enterSticky =
      theadRect.top < top - 0.5 && tableRect.bottom > top + headHeight;
    // Hand off to the float clone only after the real thead has fully cleared the
    // tab strip — avoids covering the first body row on Options / Stocks.
    const handoff = theadRect.bottom <= top + 0.5;
    const shouldPrepare = enterSticky;

    const wasActive = float.classList.contains("is-active");

    if (shouldPrepare) {
      const signature = headerSignature();
      const cloneCount = floatTable.querySelectorAll("th").length;
      if (
        !wasActive ||
        signature !== lastHeaderSignature ||
        cloneCount !== headerColumnCount()
      ) {
        cloneHeader();
      }

      if (isWebStandalone()) {
        floatToBody();
        float.style.position = "fixed";
        float.style.height = "auto";
        float.style.top = `${top}px`;
        float.style.zIndex = "99999";
      } else {
        dockFloat();
        float.style.position = "";
        float.style.left = "";
        float.style.width = "";
        float.style.height = "";
        float.style.zIndex = "";
        float.style.top = `${top}px`;
      }
      syncFloatHostBox();
      syncFloatTableLayout();
    } else {
      resetFloatPosition();
    }

    const showFloat = shouldPrepare && handoff && floatIsReady();
    float.classList.toggle("is-active", showFloat);
    table.classList.toggle("sfe-sticky-head-source-active", showFloat);
  };

  const scheduleUpdate = (): void => {
    if (raf) cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      raf = 0;
      update();
    });
  };

  window.addEventListener("scroll", scheduleUpdate, { passive: true, capture: true });
  window.addEventListener("resize", scheduleUpdate, { passive: true });
  hscroll?.addEventListener("scroll", onHScroll, { passive: true });
  floatScroll.addEventListener("scroll", onFloatScroll, { passive: true });

  const details = table.closest("details");
  const onDetailsToggle = (): void => scheduleUpdate();
  details?.addEventListener("toggle", onDetailsToggle);

  const theadObserver = new MutationObserver(scheduleUpdate);
  theadObserver.observe(thead, { childList: true, subtree: true, characterData: true });

  const resizeObserver = new ResizeObserver(scheduleUpdate);
  resizeObserver.observe(table);
  resizeObserver.observe(wrap);
  if (hscroll && hscroll !== wrap) resizeObserver.observe(hscroll);

  scheduleUpdate();

  return {
    sync: update,
    teardown: () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("scroll", scheduleUpdate, true);
      window.removeEventListener("resize", scheduleUpdate);
      hscroll?.removeEventListener("scroll", onHScroll);
      floatScroll.removeEventListener("scroll", onFloatScroll);
      details?.removeEventListener("toggle", onDetailsToggle);
      theadObserver.disconnect();
      resizeObserver.disconnect();
      table.classList.remove("sfe-sticky-head-source-active");
      resetFloatPosition();
      float.remove();
    },
  };
}

const teardownByRoot = new WeakMap<HTMLElement, () => void>();

/** Mount or refresh sticky headers for every table with a thead under root. */
export function syncStickyTableHeads(root: HTMLElement): void {
  teardownByRoot.get(root)?.();

  const cleanups: Array<() => void> = [];
  for (const table of root.querySelectorAll("table")) {
    if (!(table instanceof HTMLTableElement)) continue;
    if (table.closest(".recs-head-float, .sfe-sticky-table-head-float")) continue;
    if (table.closest("[data-sfe-no-sticky-head]") || table.hasAttribute("data-sfe-no-sticky-head")) {
      continue;
    }
    if (!table.tHead?.rows.length) continue;
    cleanups.push(mountStickyTableHead(table).teardown);
  }

  teardownByRoot.set(root, () => {
    for (const cleanup of cleanups) cleanup();
  });
}

/** Keep sticky headers in sync when panel content is re-rendered. */
export function observeStickyTableHeads(root: HTMLElement): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null;

  const run = (): void => {
    timer = null;
    syncStickyTableHeads(root);
  };

  const schedule = (): void => {
    if (timer) return;
    timer = setTimeout(run, 60);
  };

  const observer = new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (!(node instanceof HTMLElement)) continue;
        if (node.classList.contains("recs-head-float")) continue;
        const tables = [
          ...(node.matches("table") ? [node] : []),
          ...node.querySelectorAll("table"),
        ];
        if (!tables.length) continue;
        const needsSticky = tables.some(
          (table) =>
            !table.hasAttribute("data-sfe-no-sticky-head") &&
            !table.closest("[data-sfe-no-sticky-head]"),
        );
        if (!needsSticky) continue;
        schedule();
        return;
      }
    }
  });

  observer.observe(root, { childList: true, subtree: true });
  schedule();

  return () => {
    if (timer) clearTimeout(timer);
    observer.disconnect();
    teardownByRoot.get(root)?.();
    teardownByRoot.delete(root);
  };
}
